import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { iif, of, timer } from 'rxjs';
import { delayWhen, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { AcceptRingingCallAction, EndCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { RequestUserStatusChange } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.actions';
import { SamwinUserStatusSelectors } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.selectors';
import { SamwinUserStatusState } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.state';
import { StartLoadTestMode, StopLoadTestMode } from 'src/app/store/utility/load-test/load-test.actions';
import { Originator } from 'src/generated/signalR/originator';

export interface LoadTestStateModel {
   isLoadTestModeActive: boolean;
}

@State<LoadTestStateModel>({
   name: 'loadTest',
   defaults: {
      isLoadTestModeActive: false,
   },
})
@Injectable()
export class LoadTestState implements NgxsOnInit {
   constructor(private store: Store) {}

   @Selector([LoadTestState])
   public static isLoadTestModeEnabled(state: LoadTestStateModel): boolean {
      return state.isLoadTestModeActive;
   }

   ngxsOnInit(ctx?: StateContext<any>): any {
      this.store
         .select(LoadTestState.isLoadTestModeEnabled)
         .pipe(
            distinctUntilChanged(),
            switchMap((isLoadTestModeEnabled) =>
               iif(
                  () => isLoadTestModeEnabled,
                  this.store.select(SamwinLinesStateSelector.isPhoneRinging).pipe(
                     distinctUntilChanged(),
                     switchMap((isPhoneRinging) =>
                        iif(
                           () => isPhoneRinging,
                           of(0).pipe(
                              delayWhen(() => timer(_.random(1000, 10000))),
                              tap(() => console.warn('Auto accept call through load test!')),
                              switchMap(() => ctx.dispatch(new AcceptRingingCallAction()))
                           ),
                           of(null)
                        )
                     )
                  ),
                  of(null)
               )
            )
         )
         .subscribe();

      this.store
         .select(LoadTestState.isLoadTestModeEnabled)
         .pipe(
            distinctUntilChanged(),
            switchMap((isLoadTestModeEnabled) =>
               iif(
                  () => isLoadTestModeEnabled,
                  this.store.select(SamwinLinesStateSelector.isCallInProgress).pipe(
                     distinctUntilChanged(),
                     switchMap((isCallInProgress) =>
                        iif(
                           () => isCallInProgress,
                           of(0).pipe(
                              delayWhen(() => timer(_.random(2500, 15000))),
                              tap(() => console.warn('Auto end call through load test!')),
                              switchMap(() => ctx.dispatch(new EndCallAction()))
                           ),
                           this.store.select(SamwinUserStatusState.currentStatusInfo).pipe(
                              delayWhen(() => timer(_.random(5000, 15000))),
                              switchMap((currentStatusInfo) => {
                                 const allFollowUpStatusInfos = this.store.selectSnapshot(SamwinUserStatusSelectors.getAllFollowUpStatusInfos);
                                 const nextStatus = _(allFollowUpStatusInfos)
                                    .map((x) => x.id)
                                    .uniq()
                                    .without(currentStatusInfo?.id)
                                    .sample();
                                 console.warn('Auto change status to: ', nextStatus);
                                 return ctx.dispatch(new RequestUserStatusChange(nextStatus, Originator.User));
                              })
                           )
                        )
                     )
                  ),
                  of(null)
               )
            )
         )
         .subscribe();
   }

   @Action(StartLoadTestMode)
   public startLoadTestMode(ctx: StateContext<LoadTestStateModel>, action: StartLoadTestMode) {
      ctx.patchState({
         isLoadTestModeActive: true,
      });
   }

   @Action(StopLoadTestMode)
   public stopLoadTestMode(ctx: StateContext<LoadTestStateModel>, action: StopLoadTestMode) {
      ctx.patchState({
         isLoadTestModeActive: false,
      });
   }
}
